// Angular
import { Inject, Injectable } from '@angular/core';
import { TELEMETRY_HTTP_CLIENT, TelemetryHttpClient } from '@rccl/telemetry-angular';
import { catchError, take } from 'rxjs/operators';

// NGRX
import { Store, select } from '@ngrx/store';

// OLCI
import * as utils from '../../../util';
import * as fromRoot from '../../../reducers';
import { GuestService } from './guest.service';
import { GatingServices } from './gating.services';
import { BookingService } from './booking.service';
import { GuestDetailsService } from './guest-details.service';
import { AnalyticsData } from '../../../models/analytics-data';
import { ReservationRetrievalResponse } from '../../../models';
import { constants, analyticsConstants } from '../../../constants';
import { TealiumService } from './tealium.service';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { APIGEE_APP_KEY } from '~environments/environment';
import { of } from 'rxjs';
import { ErrorService } from './error.service';
import { TelemetryErrorData } from '../../../shared/interfaces';

@Injectable()
export class AnalyticsService {
  _session: ReservationRetrievalResponse;

  private readonly API_HEADERS = new HttpHeaders()
  .set('X-Requested-With', 'XMLHttpRequest')
  .set('AppKey', APIGEE_APP_KEY);

  private  readonly REQUEST_OPTIONS = {
    headers: this.API_HEADERS,
    params: new HttpParams()
  };

  constructor(
    private readonly store: Store<fromRoot.AppState>,
    private readonly gatingService: GatingServices,
    private readonly bookingService: BookingService,
    private readonly guestService: GuestService,
    private readonly guestDetailsService: GuestDetailsService,
    private readonly tealiumService: TealiumService,
    @Inject(TELEMETRY_HTTP_CLIENT) private readonly http: TelemetryHttpClient,
    private readonly errorService: ErrorService
  ) {}

  getReservationRetrievalResponse(): ReservationRetrievalResponse {
    return this.bookingService.snapshot;
  }

  getCurrentLanguageCode(): any {
    let result;
    this.store
      .pipe(select(fromRoot.getLanguageCode), take(1))
      .subscribe((res) => {
        result = res;
      });
    return result;
  }

  getCountryCode(): string | null {
    const cookieWucNonProd = utils.readCookie(constants.COOKIES.GA.WUC_NONPROD);
    const cookieWuc = utils.readCookie(constants.COOKIES.GA.WUC);
    return cookieWucNonProd && cookieWucNonProd !== ''
      ? cookieWucNonProd
      : cookieWuc;
  }

  getBrandCode(): string {
    const reservationRetrievalResponse = this.getReservationRetrievalResponse();
    if (!reservationRetrievalResponse) {
      return 'RCI';
    }
    if (reservationRetrievalResponse.brandCode === 'C') {
      return 'CEL';
    } else if (reservationRetrievalResponse.brandCode === 'Z') {
      return 'AZA';
    }
    return 'RCI';
  }

  getAnalyticsDataForTracking(
    sourcePage: any,
    ctaLocation: any
  ): AnalyticsData | null {
    let guestId = '';
    const reservationdata = this.gatingService.getReservationData();
    if (!reservationdata) {
      return null;
    }
    const guestInfo = this.guestService.getGuest(
      parseInt(reservationdata.guestId, 10)
    );
    const isGuestANewUser =
      guestInfo && !this.guestService.isGuestDetailComplete(guestInfo);

    this.guestDetailsService
      .selectedGuestId()
      .pipe(take(1))
      .subscribe((gId) => {
        guestId = gId
          ? gId.toString()
          : constants.DEFAULT_GUEST_ID_FOR_TRACKING.ID;
      });

    const reservationRetrievalResponse = this.getReservationRetrievalResponse();
    return {
      sourcePage: sourcePage,
      ctalocation: ctaLocation,
      shipCode: reservationdata.shipCode,
      saildate: `${reservationdata.departureMonth}/${reservationdata.departureDay}/${reservationdata.departureYear}`,
      bkgid: reservationdata.bookingId,
      paxid: reservationdata.guestId,
      vdsId: reservationdata.vdsId,
      voyageId: `${reservationdata.shipCode}${reservationdata.departureYear}${reservationdata.departureMonth}${reservationdata.departureDay}`,
      bookingDesc: reservationRetrievalResponse
        ? `${reservationRetrievalResponse.cruiseName}`.replace(/ /g, '_')
        : '',
      channel: analyticsConstants.APP_NAME,
      bookingStatus: analyticsConstants.BOOKING_STATUS.NEW_TO_CRUISE,
      userType: isGuestANewUser
        ? analyticsConstants.USER_TYPE.NEW
        : analyticsConstants.USER_TYPE.REPEAT,
      previousPage:
        window.sessionStorage.getItem(
          constants.SESSION_STORAGE_KEYS.PREVIOUS_PAGE
        ) || '',
      language: this.getCurrentLanguageCode().toLowerCase() || 'en',
      country: this.getCountryCode() || '',
      olciPassengerIds: guestId,
      siteType:
        window.sessionStorage.getItem(
          constants.SESSION_STORAGE_KEYS.SCREEN_WIDTH
        ) || '',
      scode_version: '10.0.0|JS-2.20.0|VI-4.4.0|20200406',
      brandID: this.getBrandCode(),
    };
  }
  getAnalyticsDataForMessagingCenter() {
    const analyticsData = this.getAnalyticsDataForTracking(null, null);
    const currentPage=this.tealiumService.getCurrentPage();
    return {
      siteSection: analyticsData?.channel,
      brand: analyticsData?.brandID,
      country: analyticsData?.country,
      language: analyticsData?.language,
      loginStatus: constants.LOGIN_STATUS.LOGGED_IN,
      appName: analyticsData?.channel,
      referringLinkLocation: currentPage,
      currentPage: currentPage,
      vdsID: analyticsData?.vdsId,
      shipCode: analyticsData?.shipCode,
      departureDate: analyticsData?.saildate,
      cruiseBookingID: analyticsData?.bkgid,
      passengerId: analyticsData?.paxid,
    };
  }

  public updateKoddiAnalytics(trackingUrl: string): void {
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Koddi analytics failed',
      lookup: 'GC-043-GET-KODDI-BEACON-SVC-FAILED',
    };
    const { params: _params, ...options } = this.REQUEST_OPTIONS;
    this.http.get(trackingUrl, {
      ...options,
      responseType: 'text'
    })
    .pipe(
      take(1),  
      catchError(err => {
        this.errorService.defaultTelemetryHttpCatch(
          err,
          telemetryErrorData,
          trackingUrl
        );
        return of(null);
      })
    )
    .subscribe();
  }
}
